import React from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Typography, Tabs } from 'antd';

import requireAuth from '../components/hocs/requireAuth';
import AdminTemplate from '../layout/AdminTemplate';
import { useEvent } from '../features/events/hooks/useEvent';
import { PreRenderTokenPage, TeamMembersPage, AuditLogPage, CloudflareTokensPage } from '../features/SecurityAndAccess';
import { isSSOSite } from '../features/auth/keycloakAuth';
import { useCfTokensTabVisible } from '../features/cloudflare-integration';

const META_TAGS = {
  title: 'Security & Access - Prerender.io',
};

const KEY_LABEL_MAP = {
  'prerender-token': 'Prerender Token',
  'team-members': 'Team Members',
  'audit-log': 'Audit Log',
  'cloudflare-tokens': 'Cloudflare Tokens',
};

function SecurityAndAccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { track } = useEvent();
  const cloudflareTokensTabVisible = useCfTokensTabVisible();

  const tabs = React.useMemo(
    () =>
      [
        {
          key: 'prerender-token',
          label: KEY_LABEL_MAP['prerender-token'],
          component: PreRenderTokenPage,
        },
        {
          key: 'team-members',
          label: KEY_LABEL_MAP['team-members'],
          component: TeamMembersPage,
          hide: isSSOSite(),
        },
        {
          key: 'audit-log',
          label: KEY_LABEL_MAP['audit-log'],
          component: AuditLogPage,
        },
        {
          key: 'cloudflare-tokens',
          label: KEY_LABEL_MAP['cloudflare-tokens'],
          component: CloudflareTokensPage,
          hide: !cloudflareTokensTabVisible,
        },
      ].filter((tab) => !tab.hide),
    [cloudflareTokensTabVisible]
  );

  const activeTab = React.useMemo(() => location.pathname.split('/').pop(), [location.pathname]);

  const onTabClick = (tabKey) => {
    track(`${KEY_LABEL_MAP[tabKey]} tab viewed`);
    // Absolute: relative targets resolve against the full splat match in react-router 7.
    navigate(`/security/${tabKey}`);
  };

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <Typography.Title level={2}>Security and Access</Typography.Title>
      <Typography.Paragraph type="secondary">
        This section allows you to manage security related configurations, generate access token, invite team members
        and more.
      </Typography.Paragraph>

      <Tabs activeKey={activeTab} items={tabs} onTabClick={onTabClick} />

      <Routes>
        {tabs.map(({ key, component: Component }) => (
          <Route path={key} key={key} element={<Component />} />
        ))}
        {/* Relative is safe here: path="" only matches when the splat is empty. */}
        <Route path="" element={<Navigate to={tabs[0].key} />}></Route>
      </Routes>
    </AdminTemplate>
  );
}

export default requireAuth(SecurityAndAccess);
